/**
 * api/workspaceRefresh.ts
 * Tiny decoupling seam between the axios layer and the live workspace hook.
 *
 * The shared axios instance calls `triggerWorkspaceRefresh()` after every
 * successful mutating request (POST/PATCH/PUT/DELETE) to the hire API, so the
 * sidebar counts + AI-token meter + lists update the instant something changes
 * — even when the SSE stream is unavailable (proxy buffering, multi-replica
 * API, dropped connection). `useAiUsage` registers the actual refetch here.
 *
 * This module imports nothing, so axiosInstance ↔ useAiUsage never form an
 * import cycle.
 */
type RefreshFn = () => void;

let refreshFn: RefreshFn | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
let streamLive = false;

/** The live-stats hook registers its snapshot refetch here (once). */
export function registerWorkspaceRefresh(fn: RefreshFn): void {
  refreshFn = fn;
}

/**
 * The live-stats hook reports whether its SSE stream is currently open.
 *
 * When it IS open the server already pushes a fresh snapshot after every
 * mutation (see notifyWorkspace in sk-hire-api), so a post-mutation refetch is a
 * duplicate request for data that is already on its way. Every POST used to
 * cost an extra /workspace GET on top of the push — a single "add candidate"
 * (extract-file → lookup → create → consent) fired several.
 */
export function setWorkspaceStreamLive(live: boolean): void {
  streamLive = live;
}

/**
 * Request a workspace refresh. Debounced (trailing) so a burst of mutations
 * (e.g. a bulk import firing many requests) collapses into a single refetch.
 *
 * NO-OP while the SSE stream is open — the push covers it. This is the fallback
 * path for a dead/unsupported stream only.
 */
export function triggerWorkspaceRefresh(delayMs = 250): void {
  if (!refreshFn) return;
  if (streamLive) return;
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => {
    timer = null;
    refreshFn?.();
  }, delayMs);
}
